import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiShoppingBag, FiHeart, FiSearch, FiMenu, FiX } from 'react-icons/fi';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { useUIStore } from '../store/useUIStore';
import '../styles/Header.css';
import logo from '../orange-logo.png';

export default function Header() {
  const navigate = useNavigate();
  const cart = useStore((state) => state.cart);
  const cartItemCount = cart.length;
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const showMobileMenu = useUIStore((state) => state.showMobileMenu);
  const setShowMobileMenu = useUIStore((state) => state.setShowMobileMenu);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const products = useStore((state) => state.products);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle header hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only hide if scrolling down and we're past the hero section
      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY) {
          // Scrolling down
          setIsHeaderHidden(true);
        } else {
          // Scrolling up
          setIsHeaderHidden(false);
        }
      } else {
        setIsHeaderHidden(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const isMobile = windowWidth < 1024;

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = products.filter(product =>
      product.name.toLowerCase().includes(query) ||
      product.sku.toLowerCase().includes(query) ||
      product.category.toLowerCase().includes(query)
    ).slice(0, 8); // Limit to 8 results

    setSearchResults(results);
  }, [searchQuery, products]);

  const handleSearch = (productId) => {
    navigate(`/product/${productId}`);
    setShowSearchModal(false);
    setSearchQuery('');
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  const handleWishlistClick = () => {
    if (user) {
      navigate('/profile?tab=wishlist');
    } else {
      navigate('/login');
    }
  };

  const handleAboutClick = () => {
    navigate('/page/about');
  };

  const handleMobileMenuClose = () => {
    setShowMobileMenu(false);
    setShowUserMenu(false);
  };

  return (
    <header className={`header ${isHeaderHidden ? 'hidden' : ''}`}>
      <div className="header-wrapper">
        {isMobile ? (
          <>
            {/* Mobile Header */}
            <button
              className="mobile-menu-btn"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              title="Menu"
            >
              {showMobileMenu ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>

            {/* Center Logo */}
            <Link to="/" className="logo">
              <img src={logo} alt="HuHua Shoes" />
            </Link>

            {/* Cart Link (mobile right) */}
            <Link to="/cart" className="cart-link mobile-cart">
              <FiShoppingBag />
              {cartItemCount > 0 && (
                <span className="cart-badge">{cartItemCount}</span>
              )}
            </Link>

            {/* Wishlist Link (mobile right) */}
            <button className="wishlist-link mobile-wishlist" 
              onClick={handleWishlistClick}>
              <FiHeart />
            </button>

            {/* Mobile Menu */}
            {showMobileMenu && (
              <nav className="mobile-menu">
                {/* Navigation Links */}
                <Link
                  to="/products"
                  className="mobile-menu-link"
                  onClick={handleMobileMenuClose}
                >
                  Shoes
                </Link>
                <Link
                  to="/gallery"
                  className="mobile-menu-link"
                  onClick={handleMobileMenuClose}
                >
                  Gallery
                </Link>

                {/* Search */}
                <button
                  className="mobile-menu-link"
                  onClick={() => {
                    setShowSearchModal(true);
                    handleMobileMenuClose();
                  }}
                >
                  Search
                </button>

                {/* About */}
                <button
                  className="mobile-menu-link"
                  onClick={() => {
                    handleAboutClick();
                    handleMobileMenuClose();
                  }}
                >
                  About us
                </button>

                {/* Divider */}
                <div className="mobile-menu-divider" />

                {/* User Menu */}
                {!user ? (
                  <Link
                    to="/login"
                    className="mobile-menu-link mobile-menu-auth"
                    onClick={handleMobileMenuClose}
                  >
                    Log In
                  </Link>
                ) : (
                  <>
                    <div className="mobile-menu-user-info">
                      <p className="user-email">{user.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      className="mobile-menu-link"
                      onClick={handleMobileMenuClose}
                    >
                      My Profile
                    </Link>
                    <Link
                      to="/profile"
                      className="mobile-menu-link"
                      onClick={handleMobileMenuClose}
                    >
                      Order History
                    </Link>
                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        className="mobile-menu-link"
                        onClick={handleMobileMenuClose}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <button
                      className="mobile-menu-link mobile-menu-logout"
                      onClick={() => {
                        handleLogout();
                        handleMobileMenuClose();
                      }}
                    >
                      Log Out
                    </button>
                  </>
                )}

              </nav>
            )}
          </>
        ) : (
          <>
            {/* Desktop Header */}
            <nav className="nav-left">
              <Link to="/products" className="nav-link">Shoes</Link>
              <Link to="/about" className="nav-link">About us</Link>
            </nav>

            {/* Center Logo */}
            <Link to="/" className="logo">
              <img src={logo} alt="HuHua Shoes" />
            </Link>

            {/* Right Navigation & Cart */}
            <nav className="nav-right">
              {/* Auth Links */}
              {!user ? (
                <Link to="/login" className="nav-link nav-btn nav-btn-login">
                  Log In
                </Link>
              ) : (
                <div className="user-menu">
                  <button
                    className="user-btn"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    title={user.name}
                  >
                    <span className="user-name">{user.name.split(' ')[0]}</span>
                  </button>

                  {showUserMenu && (
                    <div className="user-dropdown">
                      <div className="user-dropdown-header">
                        <p className="user-email">{user.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className="user-dropdown-link"
                        onClick={() => setShowUserMenu(false)}
                      >
                        👤 My Profile
                      </Link>
                      <Link
                        to="/profile"
                        className="user-dropdown-link"
                        onClick={() => setShowUserMenu(false)}
                      >
                        📦 Order History
                      </Link>
                      {user.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="user-dropdown-link"
                          onClick={() => setShowUserMenu(false)}
                        >
                          ⚙️ Admin Dashboard
                        </Link>
                      )}
                      <button
                        className="user-dropdown-link logout-link"
                        onClick={handleLogout}
                      >
                        🚪 Log Out
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Search Button */}
              <button
                className="search-link"
                onClick={() => setShowSearchModal(true)}
                title="Search products"
              >
                <FiSearch />
              </button>

              {/* Wishlist Button */}
              <button
                className="wishlist-link"
                onClick={handleWishlistClick}
                title={user ? 'Your Wishlist' : 'Sign in to view wishlist'}
              >
                <FiHeart />
              </button>

              {/* Cart Link */}
              <Link to="/cart" className="cart-link">
                <FiShoppingBag />
                {cartItemCount > 0 && (
                  <span className="cart-badge">{cartItemCount}</span>
                )}
              </Link>
            </nav>
          </>
        )}
      </div>

      {/* Search Modal */}
      {showSearchModal && (
        <div className="search-modal-overlay" onClick={() => setShowSearchModal(false)}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, SKU, or category..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button
                className="search-close"
                onClick={() => setShowSearchModal(false)}
                title="Close search"
              >
                ✕
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((product) => (
                  <button
                    key={product.id}
                    className="search-result-item"
                    onClick={() => handleSearch(product.id)}
                  >
                    <div className="result-image">
                      {product.images && product.images.length > 0 ? (
                        <img src={product.images[0].image_url} alt={product.name} />
                      ) : (
                        <div className="result-image-placeholder">No image</div>
                      )}
                    </div>
                    <div className="result-info">
                      <div className="result-name">{product.name}</div>
                      <div className="result-meta">
                        <span className="result-sku">{product.sku}</span>
                        <span className="result-category">{product.category}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {searchQuery.trim() !== '' && searchResults.length === 0 && (
              <div className="search-no-results">
                No products found for "{searchQuery}"
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
